import { CboRole } from '@cbo/shared-library';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import Header from '../../components/Header/Header';
import routes from '../../constants/routes';
import { useUsers } from '../../contexts/userContext';
import isPermitted from '../../lib/permissions';
import ScheduledReportsTabs from './ScheduledReportsTabs';
import ScheduledReportsCountCards from './ScheduledReportsCountCards';
import { useScheduledReportsQuery } from '../../admin/requests/queries';
import { filterReports } from '../utils/ViewScheduleReportUtil';
import { FilterFormValues } from '../types';
import {
  breadcrumbStyles,
  rootGridSx,
  scheduleReportsPageHeaderSx,
  scheduleReportButtonContainerSx,
  scheduleReportButtonSx,
  detailsPageSpacingSx,
} from '../styles';

function ScheduledReportsPage() {
  const { t } = useTranslation();
  const { t: scheduledReportsT } = useTranslation('translation', {
    keyPrefix: 'admin.scheduledReports',
  });
  const user = useUsers();
  const history = useHistory();
  const pageFocusRef = useRef<HTMLDivElement>(null);

  const { data: scheduledReportsData, isLoading } = useScheduledReportsQuery(
    user?.fullyAuthenticated === 'authenticated'
  );

  // Memoize reports extraction to prevent unnecessary re-renders
  const reports = useMemo(() => scheduledReportsData?.schedules ?? [], [scheduledReportsData?.schedules]);

  // Determine if user is admin based on roles
  const isAdmin = isPermitted(user, [CboRole.ADMIN_DASHBOARD_VIEWER]);

  // Search and filter state
  const [search, setSearch] = useState('');

  // Form context for filters (no validation needed for optional filters)
  const formContext = useForm<FilterFormValues>({
    mode: 'onChange',
    defaultValues: {
      statuses: [],
      occurrences: [],
      sites: [],
      scheduledBy: [],
    },
  });

  const statuses = formContext.watch('statuses') || [];
  const occurrences = formContext.watch('occurrences') || [];
  const sites = formContext.watch('sites') || [];
  const scheduledBy = formContext.watch('scheduledBy') || [];

  const selectedStatusIds = statuses.map((s) => s.id).filter((id): id is string => id !== undefined);
  const selectedOccurrenceIds = occurrences.map((o) => o.id).filter((id): id is string => id !== undefined);
  const selectedSiteIds = sites.map((s) => s.id).filter((id): id is string => id !== undefined);
  const selectedScheduledByIds = scheduledBy.map((u) => u.id).filter((id): id is string => id !== undefined);

  // Filter reports based on search and selected filters
  const filteredReports = useMemo(
    () =>
      filterReports(reports, {
        search,
        selectedStatusIds,
        selectedOccurrenceIds,
        selectedSiteIds,
        selectedScheduledByIds,
      }),
    [reports, search, selectedStatusIds, selectedOccurrenceIds, selectedSiteIds, selectedScheduledByIds]
  );

  const handleScheduleReport = () => {
    // Navigate to schedule report page
    history.push(routes.ADD_SCHEDULE_REPORTS.replace(':reportId', '0'));
  };

  if (isLoading) {
    return (
      <Grid container alignItems='center' justifyContent='center' data-testid='spinner-container'>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid>
      <Breadcrumbs
        breadcrumbLabels={[t('navItems.reports'), scheduledReportsT('title')]}
        linkLocationOverride={routes.ALL_REPORTS}
        styleOverrides={breadcrumbStyles}
      />
      <Grid sx={rootGridSx} ref={pageFocusRef} tabIndex={-1} data-testid='scheduled-reports-container'>
        <Grid container justifyContent='space-between' alignItems='flex-start' sx={scheduleReportsPageHeaderSx}>
          <Grid item xs={8}>
            <Header
              maxDescriptionWidth='100%'
              dataTestId='scheduled-reports-header'
              title={scheduledReportsT('title')}
              description={scheduledReportsT('titleContent')}
            />
          </Grid>
          <Grid item sx={scheduleReportButtonContainerSx}>
            <Button
              variant='contained'
              onClick={handleScheduleReport}
              data-testid='schedule-report-button'
              sx={scheduleReportButtonSx}
            >
              {scheduledReportsT('scheduleReport')}
            </Button>
          </Grid>
        </Grid>

        {/* Count Cards */}
        <Grid item xs={12} sx={detailsPageSpacingSx}>
          <ScheduledReportsCountCards scheduledReportsData={scheduledReportsData} isLoading={isLoading} />
        </Grid>

        <ScheduledReportsTabs
          reports={filteredReports}
          isAdmin={isAdmin}
          formContext={formContext}
          search={search}
          onSearchChange={setSearch}
        />
      </Grid>
    </Grid>
  );
}

export default ScheduledReportsPage;
